import { TrendingUp, TrendingDown, Calculator, Target, Banknote, Calendar, Droplets, Flame, Info, X, FileText, AlertTriangle, BarChart3, ShoppingCart, Repeat } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

function InfoPopup({ onClose }) {
    const { t, formatCurrency } = useLanguage();
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-bold text-gray-900">{t('info_title').replace('{0}', formatCurrency(0.06))}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <p className="text-gray-600 mb-4">{t('info_desc').replace('{0}', formatCurrency(0.06))}</p>
                <div className="space-y-3 mb-4">
                    <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                        <div>
                            <div className="font-medium text-gray-900">{t('info_gas')}</div>
                            <div className="text-xs text-gray-500">{t('info_gas_desc')}</div>
                        </div>
                        <span className="font-bold text-gray-900">{formatCurrency(0.035)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                        <div>
                            <div className="font-medium text-gray-900">{t('info_water')}</div>
                            <div className="text-xs text-gray-500">{t('info_water_desc')}</div>
                        </div>
                        <span className="font-bold text-gray-900">{formatCurrency(0.010)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                        <div>
                            <div className="font-medium text-gray-900">{t('info_electricity')}</div>
                            <div className="text-xs text-gray-500">{t('info_electricity_desc')}</div>
                        </div>
                        <span className="font-bold text-gray-900">{formatCurrency(0.015)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-amber-100 rounded-lg border border-amber-300">
                        <span className="font-bold text-gray-900">{t('info_total')}</span>
                        <span className="font-bold text-amber-700">{formatCurrency(0.06)}</span>
                    </div>
                </div>
                <p className="text-xs text-gray-500">{t('info_disclaimer').replace('{0}', formatCurrency(0.42))}</p>
            </div>
        </div>
    );
}

function ResultCard({ label, value, icon: Icon, variant = 'default', subtext }) {
    const variants = {
        default: 'bg-[#F5ECD7]/40',
        profit: 'bg-[#4CAF50]/10 border border-[#4CAF50]/20',
        loss: 'bg-red-50 border border-red-200',
        highlight: 'bg-[#3D3B00] text-white',
        warning: 'bg-amber-50 border border-amber-200'
    };
    const textVariants = {
        default: 'text-[#3D3B00]',
        profit: 'text-[#4CAF50]',
        loss: 'text-red-600',
        highlight: 'text-white',
        warning: 'text-amber-700'
    };
    const iconVariants = {
        default: 'text-[#3D3B00]/40',
        profit: 'text-[#4CAF50]',
        loss: 'text-red-400',
        highlight: 'text-[#F5ECD7]/60',
        warning: 'text-amber-500'
    };
    const labelVariants = {
        default: 'text-[#3D3B00]/50',
        profit: 'text-[#4CAF50]/70',
        loss: 'text-red-400',
        highlight: 'text-[#F5ECD7]/60',
        warning: 'text-amber-600'
    };

    return (
        <div className={`rounded-xl p-4 ${variants[variant]} transition-all duration-300 hover:scale-[1.02]`}>
            <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${iconVariants[variant]}`} />
                <span className={`text-xs uppercase tracking-wider font-semibold ${labelVariants[variant]}`}>
                    {label}
                </span>
            </div>
            <div className={`text-2xl font-bold font-['Outfit'] tabular-nums ${textVariants[variant]}`}>
                {value}
            </div>
            {subtext && (
                <div className={`text-xs mt-1 ${variant === 'highlight' ? 'text-gray-400' : 'text-gray-500'}`}>{subtext}</div>
            )}
        </div>
    );
}

export function ResultsPanel({ results }) {
    const [showExploitatieInfo, setShowExploitatieInfo] = useState(false);
    const { t, formatCurrency } = useLanguage();

    const profitVariant = results.nettoWinstMaand >= 0 ? 'profit' : 'loss';
    const profitIcon = results.nettoWinstMaand >= 0 ? TrendingUp : TrendingDown;

    return (
        <div className="bg-white/80 text-[#3D3B00] rounded-2xl p-6 lg:p-8 shadow-lg border border-[#3D3B00]/10" data-testid="results-panel">
            <div className="mb-6">
                <h2 className="text-2xl font-bold font-['Outfit'] mb-2 text-[#3D3B00]">{t('results_title')}</h2>
                <p className="text-[#3D3B00]/50 text-sm">
                    {results.aantalDouches} {t('results_readers')} • {results.douchebeurtenPerMaand} {t('results_showers_month')} ({t('results_corrected')})
                </p>
            </div>

            {/* Kopen vs Leasen vergelijking */}
            <div className="mb-6 grid grid-cols-2 gap-3" data-testid="koop-vs-lease-panel">
                <div className={`rounded-2xl p-4 border-2 ${results.isProfitableKoop ? 'bg-[#3D3B00] border-[#3D3B00] text-white' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="scenario-kopen">
                    <div className="flex items-center gap-2 mb-2">
                        <ShoppingCart className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-wider font-semibold">Kopen</span>
                    </div>
                    <div className="text-2xl font-bold font-['Outfit']" data-testid="kopen-winst-mnd">
                        {formatCurrency(results.nettoWinstMaandKoop)}
                    </div>
                    <div className="text-xs opacity-70 mb-3">netto winst/mnd</div>
                    <div className="space-y-1 text-xs pt-2 border-t border-white/10">
                        <div className="flex justify-between opacity-80">
                            <span>Pinterminal ({results.aantalDouches}x)</span>
                            <span className="font-semibold">{formatCurrency(results.koopprijsTerminal)}</span>
                        </div>
                        {results.muurbevestigingKosten > 0 && (
                            <div className="flex justify-between opacity-80">
                                <span>Muurbevestiging ({results.aantalDouches}x)</span>
                                <span className="font-semibold">{formatCurrency(results.muurbevestigingKosten)}</span>
                            </div>
                        )}
                        <div className="flex justify-between opacity-80 pt-1 border-t border-white/10">
                            <span>Investering totaal</span>
                            <span className="font-semibold">{formatCurrency(results.koopInvestering)}</span>
                        </div>
                        <div className="flex justify-between opacity-80">
                            <span>Per jaar</span>
                            <span className="font-semibold">{formatCurrency(results.nettoWinstJaarKoop)}</span>
                        </div>
                        <div className="flex justify-between opacity-80">
                            <span>Terugverdientijd</span>
                            <span className="font-semibold">
                                {results.terugverdientijdMaandenKoop > 0 ? `${results.terugverdientijdMaandenKoop} mnd` : '-'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={`rounded-2xl p-4 border-2 ${results.isProfitable ? 'bg-[#4CAF50] border-[#4CAF50] text-white' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="scenario-leasen">
                    <div className="flex items-center gap-2 mb-2">
                        <Repeat className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-wider font-semibold">Leasen</span>
                    </div>
                    <div className="text-2xl font-bold font-['Outfit']" data-testid="leasen-winst-mnd">
                        {formatCurrency(results.nettoWinstMaand)}
                    </div>
                    <div className="text-xs opacity-90 mb-3">netto winst/mnd</div>
                    <div className="space-y-1 text-xs pt-2 border-t border-white/20">
                        <div className="flex justify-between opacity-90">
                            <span>Vaste kosten/mnd</span>
                            <span className="font-semibold">{formatCurrency(results.leaseKostenPerMaand)}</span>
                        </div>
                        <div className="flex justify-between opacity-90">
                            <span>Per jaar</span>
                            <span className="font-semibold">{formatCurrency(results.nettoWinstJaar)}</span>
                        </div>
                        <div className="flex justify-between opacity-90">
                            <span>Investering vooraf</span>
                            <span className="font-semibold">{formatCurrency(0)}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                {/* Realistische Correctie */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100" data-testid="bezettingsgraad-info">
                    <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="w-4 h-4 text-blue-500" />
                        <span className="text-xs uppercase tracking-wider text-blue-600 font-semibold">
                            {t('results_correction_title')}
                        </span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700">
                            {t('results_correction_desc').replace('{0}', results.douchebeurtenPerMaandRaw).replace('{1}', results.bezettingsgraad)}
                        </span>
                        <span className="text-lg font-bold text-blue-800">{results.douchebeurtenPerMaand}</span>
                    </div>
                    <div className="text-xs text-blue-500 mt-1">
                        {t('results_correction_factors')}
                    </div>
                </div>

                {/* Bruto Omzet */}
                <ResultCard
                    label={t('results_gross_revenue')}
                    value={formatCurrency(results.brutoOmzet)}
                    icon={Banknote}
                    variant="default"
                    subtext={t('results_showers_x_min_x_price').replace('{0}', results.douchebeurtenPerMaand).replace('{1}', results.gemiddeldeDouchetijd).replace('{2}', formatCurrency(results.prijsPerMinuut))}
                />

                {/* Transactiekosten */}
                <div className="bg-[#F5ECD7]/40 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Droplets className="w-4 h-4 text-[#3D3B00]/40" />
                        <span className="text-xs uppercase tracking-wider text-[#3D3B00]/50 font-semibold">
                            {t('results_transaction_costs')}
                        </span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-[#3D3B00]/60">{t('results_transactions_x_fee').replace('{0}', results.actueleTransacties).replace('{1}', formatCurrency(0.20))}</span>
                        <span className="text-lg font-bold text-[#3D3B00]/60">- {formatCurrency(results.transactiekosten)}</span>
                    </div>
                    <div className="text-xs text-[#3D3B00]/40 mt-1">{t('results_auto_deducted')}</div>
                </div>

                {/* Netto Omzet */}
                <ResultCard
                    label={t('results_net_revenue')}
                    value={formatCurrency(results.nettoOmzet)}
                    icon={Banknote}
                    variant="default"
                    subtext={t('results_receipt_per_shower').replace('{0}', formatCurrency(results.opbrengstPerDouche))}
                />

                {/* Lease kosten */}
                <div className="bg-[#F5ECD7]/40 rounded-xl p-4 space-y-3">
                    <div className="text-xs uppercase tracking-wider text-[#3D3B00]/50 font-semibold mb-3">
                        {t('results_monthly_recra')}
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-[#3D3B00]/60">
                            {t('results_lease_per_terminal').replace('{0}', results.aantalDouches)}
                        </span>
                        <span className="text-[#3D3B00] font-medium">
                            {formatCurrency(results.leasePrijsPerTerminal)} x {results.aantalDouches}
                        </span>
                    </div>
                    <div className="border-t border-[#3D3B00]/10 pt-3 flex justify-between">
                        <span className="text-[#3D3B00]/80 font-semibold">{t('results_total_recra')}</span>
                        <span className="text-[#3D3B00] font-bold">{formatCurrency(results.leaseKostenPerMaand)}</span>
                    </div>
                    <div className="text-xs text-[#3D3B00]/40 mt-1 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {t('results_contract_info')}
                    </div>
                </div>

                {/* Garantie kosten (alleen tonen als actief) */}
                {results.garantie && (
                    <div className="bg-[#4CAF50]/5 rounded-xl p-4 border border-[#4CAF50]/10" data-testid="garantie-cost">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-xs uppercase tracking-wider text-[#4CAF50]/70 font-semibold">
                                    {t('results_garantie')}
                                </span>
                            </div>
                            <span className="text-sm font-bold text-gray-700">{formatCurrency(results.garantieKosten)}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">{formatCurrency(3)} x {results.aantalDouches} {t('results_readers')}</div>
                    </div>
                )}

                {/* MRG - Inbegrepen gebruik (alleen tonen als actief) */}
                {results.mrgActief && (
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200" data-testid="mrg-warning">
                        <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            <span className="text-xs uppercase tracking-wider text-amber-600 font-semibold">
                                {t('results_mrg_title')}
                            </span>
                        </div>
                        <div className="text-sm text-amber-700 mb-2">
                            {t('results_mrg_desc').replace('{0}', results.mrgMinTransacties).replace('{1}', results.actueleTransacties).replace('{2}', results.mrgTekort)}
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-amber-200">
                            <span className="text-amber-700 font-semibold text-sm">{t('results_mrg_extra')}</span>
                            <span className="text-amber-800 font-bold text-lg">{formatCurrency(results.mrgBijbetaling)}</span>
                        </div>
                        <div className="text-xs text-amber-500 mt-1">
                            {t('results_mrg_calc').replace('{0}', results.mrgTekort).replace('{1}', formatCurrency(0.10))}
                        </div>
                    </div>
                )}

                {/* Exploitatiekosten */}
                <div className="rounded-xl p-3 border border-[#3D3B00]/10">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Flame className="w-3.5 h-3.5 text-[#3D3B00]/40" />
                            <span className="text-xs text-[#3D3B00]/50">{t('results_own_costs')}</span>
                            <button onClick={() => setShowExploitatieInfo(true)} className="text-[#3D3B00]/40 hover:text-[#3D3B00]/70 transition-colors" data-testid="exploitatie-info-btn">
                                <Info className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <span className="text-sm font-medium text-[#3D3B00]/50">{formatCurrency(results.exploitatieKosten)}</span>
                    </div>
                    <div className="text-xs text-[#3D3B00]/40 mt-1 ml-5.5">
                        {results.totaalMinutenPerMaand} min x {formatCurrency(0.06)}
                    </div>
                </div>
                
                {showExploitatieInfo && <InfoPopup onClose={() => setShowExploitatieInfo(false)} />}

                {/* Netto Winst Maand */}
                <ResultCard
                    label={t('results_net_profit_month')}
                    value={formatCurrency(results.nettoWinstMaand)}
                    icon={profitIcon}
                    variant={profitVariant}
                    subtext={results.mrgActief 
                        ? t('results_profit_incl_mrg')
                        : t('results_profit_per_shower').replace('{0}', formatCurrency(results.douchebeurtenPerMaand > 0 ? results.nettoWinstMaand / results.douchebeurtenPerMaand : 0))
                    }
                />

                {/* Netto Winst Jaar */}
                <ResultCard
                    label={t('results_net_profit_year')}
                    value={formatCurrency(results.nettoWinstJaar)}
                    icon={Calendar}
                    variant={profitVariant}
                    subtext={t('results_year_months').replace('{0}', results.maandenOpen)}
                />

                {/* Break-even */}
                <ResultCard
                    label={t('results_breakeven')}
                    value={`${formatCurrency(results.breakevenPrijsPerMinuut)}/min`}
                    icon={Target}
                    variant="highlight"
                    subtext={t('results_breakeven_desc')}
                />
            </div>

            {/* Status indicator */}
            <div className={`mt-6 p-4 rounded-xl text-center ${
                results.isProfitable 
                    ? 'bg-[#4CAF50]/10 border border-[#4CAF50]/20' 
                    : 'bg-amber-50 border border-amber-200'
            }`}>
                {results.isProfitable ? (
                    <div className="flex items-center justify-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#4CAF50]" />
                        <span className="font-semibold text-[#4CAF50]">{t('results_profitable')}</span>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2">
                        <Calculator className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-amber-600">{t('results_not_profitable')}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
